import { useCallback, useEffect, useRef, useState } from 'react'
import { ArrowRightLeft, ChevronLeft, ChevronRight, History, LogIn, LogOut, PlusCircle, RotateCcw, ShieldCheck, ShieldX, Tag, Target, XCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { dayLabel, hhmmss } from '../utils/format'
import type { JournalActivity, JournalEvent } from '../types'

const KINDS: Record<string, { icon: typeof LogIn; label: string; tone?: string }> = {
  ENTRY: { icon: LogIn, label: 'Entry requested' },
  /*
    9.63. An ADD, not the entry. Toned as an edit rather than a neutral
    fill, because deciding to carry more risk mid-trade is the thing a
    review is looking for and it should not read like the original entry.
  */
  SCALED_IN: { icon: PlusCircle, label: 'Added to the position', tone: 'edit' },
  EXIT: { icon: LogOut, label: 'Exit requested' },
  SQUARE_OFF: { icon: LogOut, label: 'Square-off requested' },
  MODIFY: { icon: RotateCcw, label: 'Modification requested', tone: 'edit' },
  STOP_SET: { icon: ShieldCheck, label: 'Stop armed' },
  STOP_CHANGED: { icon: ShieldCheck, label: 'Stop updated', tone: 'edit' },
  STOP_HIT: { icon: ShieldX, label: 'Stop loss hit', tone: 'loss' },
  TARGET_SET: { icon: Target, label: 'Target armed' },
  TARGET_CHANGED: { icon: Target, label: 'Target updated', tone: 'edit' },
  TARGET_HIT: { icon: Target, label: 'Target reached', tone: 'profit' },
  LEG_TRANSFERRED: { icon: ArrowRightLeft, label: 'Leg grouped', tone: 'edit' },
  LEG_REMOVED: { icon: ArrowRightLeft, label: 'Leg removed', tone: 'edit' },
  TRADE_EXIT: { icon: LogOut, label: 'Trade exited', tone: 'profit' },
  ROLE_TAGGED: { icon: Tag, label: 'Leg tagged' },
  ROLLOVER: { icon: ArrowRightLeft, label: 'Leg rolled over', tone: 'edit' },
}
const ORIGINS: Record<string, string> = {
  CHAIN_CLICK: 'Option chain', DEPLOYMENT: 'Strategy deployment', POSITIONS_PANE: 'Positions panel',
  GUARDIAN: 'Risk guardian', SYSTEM: 'System',
}
const FAILED = new Set(['REFUSED', 'REJECTED', 'CANCELLED'])

export function JournalTimeline({ events, activity }: { events: readonly JournalEvent[]; activity?: JournalActivity }) {
  const stopChanges = events.filter((event) => event.kind === 'STOP_CHANGED').length
  const targetChanges = events.filter((event) => event.kind === 'TARGET_CHANGED').length
  const track = useRef<HTMLOListElement>(null)
  const [scroll, setScroll] = useState({ previous: false, next: false })
  const measure = useCallback(() => {
    const element = track.current
    if (element) setScroll({ previous: element.scrollLeft > 1, next: element.scrollLeft + element.clientWidth < element.scrollWidth - 1 })
  }, [])

  useEffect(() => {
    measure()
    const element = track.current
    if (!element || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [events, measure])

  const move = (direction: number) => {
    const element = track.current
    element?.scrollBy({ left: direction * element.clientWidth * .8, behavior: 'auto' })
  }

  return (
    <section className="jn-panel jn-detail-timeline" aria-label="Trade timeline">
      <header className="jn-panel-heading">
        <div className="flex items-center gap-2"><History size={14} className="text-primary" /><h3>Trade timeline</h3></div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {activity && <>
            {activity.initialLegs > 0 && <span className="jn-badge" title="Distinct legs with entry fills in this trade">Initial {activity.initialLegs}</span>}
            {activity.addedLegs > 0 && <span className="jn-badge" title="Distinct legs manually added after entry">Added {activity.addedLegs}</span>}
            {/* 9.63. Legs the trader chose to carry MORE of, mid-trade. */}
            {activity.scaledLegs > 0 && <span className="jn-badge" title="Distinct legs the position was added to after it was open">Scaled in {activity.scaledLegs}</span>}
            {activity.removedLegs > 0 && <span className="jn-badge" title="Distinct legs moved out of this trade">Removed {activity.removedLegs}</span>}
            {activity.manualExitLegs > 0 && <span className="jn-badge" title="Distinct legs with confirmed manual exit fills">Manual exits {activity.manualExitLegs}</span>}
            {activity.stopHitLegs > 0 && <span className="jn-badge" title="Distinct legs with confirmed stop-loss fills">SL hits {activity.stopHitLegs}</span>}
          </>}
          {stopChanges > 0 && <span className="jn-badge" title={`${stopChanges} recorded stop changes`}>SL ×{stopChanges}</span>}
          {targetChanges > 0 && <span className="jn-badge" title={`${targetChanges} recorded target changes`}>TGT ×{targetChanges}</span>}
          <span className="jn-caption">{events.length} event{events.length === 1 ? '' : 's'}</span>
          {(scroll.previous || scroll.next) && <div className="jn-timeline-nav">
            <button type="button" aria-label="Earlier timeline events" disabled={!scroll.previous} onClick={() => move(-1)}><ChevronLeft size={14} /></button>
            <button type="button" aria-label="Later timeline events" disabled={!scroll.next} onClick={() => move(1)}><ChevronRight size={14} /></button>
          </div>}
        </div>
      </header>
      {events.length > 0 ? (
        <ol ref={track} className="jn-timeline" data-testid="journal-timeline" tabIndex={0} aria-label="Timeline events, earliest to latest" onScroll={measure}>
          {events.map((event, index) => {
            const style = KINDS[event.kind] ?? { icon: History, label: event.kind.replaceAll('_', ' ').toLowerCase() }
            const failed = FAILED.has(event.state)
            const Icon = failed ? XCircle : style.icon
            return (
              <li key={event.id ?? `${event.at}-${index}`} className={cn(style.tone && `is-${style.tone}`, failed && 'is-failed')}>
                <time dateTime={event.at} title={`${dayLabel(event.at)} ${hhmmss(event.at)} IST`} className="num"><strong>{hhmmss(event.at)}</strong><span>{dayLabel(event.at)}</span></time>
                <div className="jn-event-connector"><span className="jn-event-icon"><Icon size={13} /></span></div>
                <div className="jn-event-body">
                  <div className="flex items-start justify-between gap-2">
                    <h4>{event.label ?? style.label}</h4>
                    <span className={cn('jn-event-state', failed && 'is-failed')}>{event.state.toLowerCase()}</span>
                  </div>
                  {event.change && <div className="jn-event-change num"><span>{event.change.label}</span><s>{event.change.before}</s><span>→</span><strong>{event.change.after}</strong></div>}
                  {event.legLabels?.length ? <div className="jn-event-legs">{event.legLabels.map((label) => <span className="num" key={label}>{label}</span>)}</div> :
                    <p>{ORIGINS[event.origin] ?? event.origin.replaceAll('_', ' ').toLowerCase()} · {event.legCount} leg{event.legCount === 1 ? '' : 's'}</p>}
                  {event.message && <p>{event.message}</p>}
                </div>
              </li>
            )
          })}
        </ol>
      ) : <span className="jn-empty-value">No events</span>}
    </section>
  )
}
